import React, { useEffect, useMemo, useState } from 'react';
import {
    AlertCircle,
    CalendarDays,
    CheckCircle2,
    ChevronLeft,
    Gift,
    IndianRupee,
    Loader2,
    Percent,
    Tag,
    Command
} from 'lucide-react';

import styles from './DiscountMembershipForm.module.css';

const MEMBERSHIP_TYPES = [
    { id: 'BASIC', label: 'Basic', short: 'B', price: 499, serviceDiscount: 5, productDiscount: 0 },
    { id: 'SILVER', label: 'Silver', short: 'S', price: 899, serviceDiscount: 10, productDiscount: 5 },
    { id: 'GOLD', label: 'Gold', short: 'G', price: 1499, serviceDiscount: 15, productDiscount: 10 },
    { id: 'PREMIUM', label: 'Premium', short: 'P', price: 2499, serviceDiscount: 20, productDiscount: 15 },
    { id: 'VIP', label: 'VIP', short: 'V', price: 3999, serviceDiscount: 25, productDiscount: 20 }
];

const DEFAULT_TYPE = MEMBERSHIP_TYPES[2];

const defaultForm = (initialType) => ({
    membershipType: initialType || DEFAULT_TYPE.id,
    name: '',
    price: String(MEMBERSHIP_TYPES.find(t => t.id === initialType)?.price || DEFAULT_TYPE.price),
    validity: '12',
    validityType: 'Months',
    serviceDiscount: String(MEMBERSHIP_TYPES.find(t => t.id === initialType)?.serviceDiscount || DEFAULT_TYPE.serviceDiscount),
    productDiscount: String(MEMBERSHIP_TYPES.find(t => t.id === initialType)?.productDiscount || DEFAULT_TYPE.productDiscount)
});

const toNumber = (value) => {
    const number = Number(value);
    return Number.isFinite(number) ? number : 0;
};

const money = (value) =>
    `₹${toNumber(value).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

export default function DiscountMembershipForm({
    initialData,
    existingTiers = [], // List of already created tiers
    onCancel,
    onSave,
    isSaving
}) {
    const [step, setStep] = useState('FORM');
    const [error, setError] = useState('');
    const isEditMode = Boolean(initialData);

    // Filter out which tiers have already been created
    const usedTierIds = existingTiers.map(tier => tier.membershipType);

    const [formData, setFormData] = useState(() => defaultForm(DEFAULT_TYPE.id));

    // 🔥 BUG FIXED: This useEffect will ONLY run when form opens.
    // Parent re-renders will NO LONGER reset the dropdown!
    useEffect(() => {
        if (!initialData) {
            // Find the first tier that hasn't been created yet
            const firstAvailable = MEMBERSHIP_TYPES.find(c => !usedTierIds.includes(c.id))?.id || DEFAULT_TYPE.id;
            setFormData(defaultForm(firstAvailable));
            setStep('FORM');
            setError('');
        } else {
            const savedType = initialData.membershipType || initialData.tierType || initialData.planType;
            const selectedType = MEMBERSHIP_TYPES.find((type) => type.id === savedType) || DEFAULT_TYPE;

            setFormData({
                membershipType: selectedType.id,
                name: initialData.name || '',
                price: String(initialData.price ?? selectedType.price),
                validity: String(initialData.validity || 12),
                validityType: initialData.validityType || 'Months',
                serviceDiscount: String(initialData.serviceDiscount ?? selectedType.serviceDiscount),
                productDiscount: String(initialData.productDiscount ?? selectedType.productDiscount)
            });
            setStep('FORM');
            setError('');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialData]); 

    const selectedType = useMemo(
        () => MEMBERSHIP_TYPES.find((type) => type.id === formData.membershipType) || DEFAULT_TYPE,
        [formData.membershipType]
    );

    const updateField = (field, value) => {
        setFormData((current) => ({ ...current, [field]: value }));
        if (error) setError('');
    };

    const handleTypeSelect = (e) => {
        const typeId = e.target.value;
        const type = MEMBERSHIP_TYPES.find(t => t.id === typeId);
        if (type) {
            setFormData(current => ({
                ...current,
                membershipType: type.id,
                price: String(type.price),
                serviceDiscount: String(type.serviceDiscount),
                productDiscount: String(type.productDiscount)
            }));
            setError('');
        }
    };

    const executeSave = async () => {
        const price = toNumber(formData.price);
        const validity = toNumber(formData.validity);
        const serviceDiscount = toNumber(formData.serviceDiscount);
        const productDiscount = toNumber(formData.productDiscount);

        if (!formData.name.trim()) return setError('Membership pass name is required.');
        if (price < 0) return setError('Enter a valid membership fee.');
        if (validity <= 0) return setError('Enter a valid membership validity period.');
        if (serviceDiscount <= 0 && productDiscount <= 0) return setError('You must offer at least one service or product discount.');
        if (serviceDiscount > 100 || productDiscount > 100) return setError('Discount values cannot exceed 100%.');

        setError('');
        const payload = { ...formData, membershipType: formData.membershipType };
        const result = await onSave(payload);

        if (result?.success) {
            setStep('GENERATED');
        }
    };

    return (
        <div className={styles.workspace}>
            <div className={styles.mainSplit}>
                <section className={styles.leftPanel}>
                    {step === 'FORM' ? (
                        <>
                            <div className={styles.formBody}>
                                <div className={styles.formIntro}>
                                    <h2>{isEditMode ? 'Manage Membership' : 'Create VIP Pass'}</h2>
                                    <p>Design a profile-based subscription. Clients pay a one-time fee to unlock automatic percentage discounts on future services.</p>
                                </div>

                                <section className={styles.formSection}>
                                    <div className={styles.formSectionLabel}>
                                        <Tag size={14} /> Tier Level
                                    </div>

                                    <div className={styles.formGroupFull}>
                                        <label htmlFor="tier-select">Select Membership Tier</label>
                                        <select
                                            id="tier-select"
                                            className={styles.sleekSelect}
                                            value={formData.membershipType}
                                            onChange={handleTypeSelect}
                                            disabled={isEditMode}
                                        >
                                            {MEMBERSHIP_TYPES.map((type) => {
                                                // 🔥 DISABLE LOGIC: If already created, grey it out
                                                const isUsed = !isEditMode && usedTierIds.includes(type.id);
                                                return (
                                                    <option key={type.id} value={type.id} disabled={isUsed}>
                                                        {type.label} Tier {isUsed ? '(Already Active)' : ''}
                                                    </option>
                                                );
                                            })}
                                        </select>
                                    </div>
                                </section>

                                <section className={styles.formSection}>
                                    <div className={styles.formSectionLabel}>
                                        <Gift size={14} /> Pass Details
                                    </div>

                                    <div className={styles.formGroupFull}>
                                        <label htmlFor="discount-name">Pass Name <span className={styles.required}>*</span></label>
                                        <input
                                            id="discount-name"
                                            type="text"
                                            className={styles.sleekInput}
                                            placeholder={`e.g. ${selectedType.label} Salon Pass`}
                                            value={formData.name}
                                            onChange={(e) => updateField('name', e.target.value)}
                                            autoComplete="off"
                                        />
                                    </div>

                                    <div className={styles.grid2}>
                                        <div className={styles.formGroup}>
                                            <label htmlFor="discount-price">Membership fee <span className={styles.required}>*</span></label>
                                            <div className={styles.inputWithIcon}>
                                                <IndianRupee size={14} />
                                                <input
                                                    id="discount-price"
                                                    type="number"
                                                    min="0"
                                                    className={styles.sleekInput}
                                                    value={formData.price}
                                                    onChange={(e) => updateField('price', e.target.value)}
                                                />
                                            </div>
                                        </div>

                                        <div className={styles.formGroup}>
                                            <label htmlFor="discount-validity">Validity <span className={styles.required}>*</span></label>
                                            <div className={styles.inputGroupSplit}>
                                                <input
                                                    id="discount-validity"
                                                    type="number"
                                                    min="1"
                                                    className={styles.splitLeft}
                                                    value={formData.validity}
                                                    onChange={(e) => updateField('validity', e.target.value)}
                                                />
                                                <select
                                                    className={styles.splitRight}
                                                    value={formData.validityType}
                                                    onChange={(e) => updateField('validityType', e.target.value)}
                                                >
                                                    <option value="Months">Months</option>
                                                    <option value="Years">Years</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </section>

                                <section className={styles.formSection}>
                                    <div className={styles.formSectionLabel}>
                                        <Percent size={14} /> Benefit Values
                                    </div>

                                    <div className={styles.grid2}>
                                        <div className={styles.formGroup}>
                                            <label htmlFor="service-discount">Service discount <span className={styles.required}>*</span></label>
                                            <div className={styles.inputWithIcon}>
                                                <input
                                                    id="service-discount"
                                                    type="number"
                                                    min="0" max="100"
                                                    className={styles.sleekInput}
                                                    value={formData.serviceDiscount}
                                                    onChange={(e) => updateField('serviceDiscount', e.target.value)}
                                                />
                                                <span className={styles.inputSuffix}>%</span>
                                            </div>
                                        </div>

                                        <div className={styles.formGroup}>
                                            <label htmlFor="product-discount">Product discount</label>
                                            <div className={styles.inputWithIcon}>
                                                <input
                                                    id="product-discount"
                                                    type="number"
                                                    min="0" max="100"
                                                    className={styles.sleekInput}
                                                    value={formData.productDiscount}
                                                    onChange={(e) => updateField('productDiscount', e.target.value)}
                                                />
                                                <span className={styles.inputSuffix}>%</span>
                                            </div>
                                        </div>
                                    </div>
                                </section>

                                {error && (
                                    <div className={styles.inlineError} role="alert">
                                        <AlertCircle size={14} />
                                        <span>{error}</span>
                                    </div>
                                )}
                            </div>

                            <footer className={styles.panelFooter}>
                                <button type="button" className={styles.secondaryBtn} onClick={onCancel} disabled={isSaving}>
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className={styles.primaryBtn} 
                                    onClick={executeSave} 
                                    disabled={isSaving || (!isEditMode && usedTierIds.length === MEMBERSHIP_TYPES.length)}
                                >
                                    {isSaving ? (
                                        <><Loader2 className={styles.spinner} size={15} /> Publishing...</>
                                    ) : (
                                        'Save & Publish'
                                    )}
                                </button>
                            </footer>
                        </>
                    ) : (
                        <div className={styles.successStateContainer}>
                            <div className={styles.successIconWrapper}>
                                <CheckCircle2 size={32} />
                            </div>
                            <span className={styles.successEyebrow}>READY FOR CLIENTS</span>
                            <h2>Membership Published</h2>
                            <p>
                                {formData.name || `${selectedType.label} Membership`} is now available for assigning to client profiles during billing.
                            </p>
                            <div className={styles.successActions}>
                                <button type="button" className={styles.primaryBtn} onClick={onCancel} style={{width: '100%'}}>
                                    Done & Back to List
                                </button>
                            </div>
                        </div>
                    )}
                </section>

                <aside className={styles.rightPanel} aria-label="Discount membership preview">
                    <div className={styles.previewGlow}></div>
                    <div className={styles.previewHeader}>
                        <div>
                            <span>LIVE PREVIEW</span>
                            <h2>Client Digital Pass</h2>
                        </div>
                        <span className={styles.livePill}><span /> Syncing</span>
                    </div>

                    <div className={styles.previewArea}>
                        {/* 🚀 GLOSS WHITE MINIMAL CARD PREVIEW */}
                        <article className={styles.vipPassCard}>
                            <div className={styles.passGlow}></div>
                            <div className={styles.passHeader}>
                                <div className={styles.passBrand}>
                                    <Command size={16} strokeWidth={2.5} />
                                    <strong>Smiify</strong>
                                </div>
                                <span className={styles.passType}>{selectedType.label}</span>
                            </div>

                            <div className={styles.passMain}>
                                <h3 className={styles.passNameTitle}>{formData.name || `${selectedType.label} VIP`}</h3>
                            </div>

                            <div className={styles.passBenefits}>
                                <div className={styles.benefitRow}>
                                    <span className={styles.benefitValue}>{formData.serviceDiscount || 0}% OFF</span>
                                    <span className={styles.benefitLabel}>Services</span>
                                </div>
                                <div className={styles.benefitRow}>
                                    <span className={styles.benefitValue}>{formData.productDiscount || 0}% OFF</span>
                                    <span className={styles.benefitLabel}>Retail</span>
                                </div>
                            </div>

                            <div className={styles.passFooter}>
                                <div className={styles.footerCol}>
                                    <span className={styles.footerLabel}>Validity</span>
                                    <span className={styles.footerValue}>
                                        <CalendarDays size={11} /> {formData.validity || 0} {formData.validityType}
                                    </span>
                                </div>
                                <div className={`${styles.footerCol} ${styles.footerColRight}`}>
                                    <span className={styles.footerLabel}>Pass Fee</span>
                                    <span className={styles.footerValueAccent}>{money(formData.price)}</span>
                                </div>
                            </div>
                        </article>
                    </div>
                </aside>
            </div>
        </div>
    );
}